import {
  getFirestore,
  initializeFirestore,
  collection,
  addDoc,
  getDocs,
  doc,
  getDoc,
  setDoc,
  query,
  where,
  DocumentData,
  Timestamp,
  serverTimestamp,
  DocumentReference,
  writeBatch
} from "firebase/firestore";
import {
  ACCOUNT_REQUEST_STATUSES,
  normalizeAccountRequestStatus,
  type AccountRequestStatus,
  type AccountRequestStatusInput,
} from "./account-request-status";
import { initFirestoreWithTransport } from "../lib/firestore-transport";
import { initializeApp } from "firebase/app";
import {
  initializeAuth,
  browserLocalPersistence,
  browserSessionPersistence,
  inMemoryPersistence,
  browserPopupRedirectResolver,
} from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { 
  initializeAppCheck, 
  ReCaptchaV3Provider, 
  getToken,
  onTokenChanged
} from "firebase/app-check";
import type { AppCheck } from "firebase/app-check";
import { appCheckConfig } from '../config/appCheck';
import { watchAppCheckOutcome } from '../lib/appCheckOutcome';
import { captureBootAuthSnapshot } from '../lib/bootAuthSnapshot';
import { installIndexedDbOpenBound } from './indexedDbOpenBound';

// Firebase configuration
const _prodFirebaseConfig = {
  apiKey: "AIzaSyDLcj-a71MhSsuiGsheSJ3ciDotdQpLDfU",
  authDomain: "pro.kidsbooked.com",
  projectId: "kb-prod-fb-proj",
  storageBucket: "kb-prod-fb-proj.appspot.com",
  messagingSenderId: "198588493338",
  appId: "1:198588493338:web:96516c5b3c81f553506ba3",
  measurementId: "G-45S84QCZM1"
};

const _stagingFirebaseConfig = {
  apiKey: "AIzaSyDuq5moz8MI4MGOBQOEzv-ubg_0ovAu7B8",
  authDomain: "kb-staging-proj.firebaseapp.com",
  projectId: "kb-staging-proj",
  storageBucket: "kb-staging-proj.firebasestorage.app",
  messagingSenderId: "355323975840",
  appId: "1:355323975840:web:e7db572333de55021ad1c5",
};

const firebaseConfig =
  import.meta.env.VITE_FIREBASE_ENV === "staging"
    ? _stagingFirebaseConfig
    : _prodFirebaseConfig;

// KB-3467: before anything can reach `_getProvider`, whose first call starts the
// heartbeat service's IndexedDB open. See ./indexedDbOpenBound.ts.
installIndexedDbOpenBound();

// Initialize Firebase
export const app = initializeApp(firebaseConfig);

// KB-3425: was `getAuth(app)`, whose default hierarchy is
// [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence]
// (@firebase/auth 12.16.0, index-d90d2ee5.js:11141). `PersistenceUserManager.create`
// filters that with `Promise.all` over each member's `_isAvailable()`, and the
// IndexedDB probe opens the database with no timeout and no `blocked` listener.
// A store that answers with NO event — measured on the sibling origin at
// 8001 ms, KB-3386 — leaves that probe pending forever, and `Promise.all`
// pending with it. The ordered list does not rescue this: it is a preference
// order, not a fallback-on-hang.
//
// `getAuth` itself returns fine — the SDK floats `_initializationPromise` on
// purpose — so nothing throws and module evaluation completes. What never
// happens is `onAuthStateChanged` firing its first callback. The app boots
// into a gate it can never leave, with no console error.
//
// ⛔ THE FIX IS THE ABSENCE OF IndexedDB FROM THIS LIST, AND THERE IS NO
// TIMEOUT ANYWHERE IN IT. Two cleverer shapes were written first — bound the
// probe and upgrade afterwards, then keep IndexedDB and wrap it in per-method
// deadlines — and BOTH reintroduced this ticket's own defect one layer down,
// destroying sessions on the HEALTHY path. ./authPersistence.ts records all
// three attempts, the SDK line numbers, and what each one rendered.
//
// The constraint they violated: a bound must never produce a value its caller
// can mistake for ground truth. A timeout handed to this SDK as `false`, `null`
// or a resolved write is acted on, destructively. The only way to satisfy that
// with certainty is to not need a bound — so the SDK is never handed IndexedDB,
// and `create` never calls `_isAvailable` or `_get` on it.
//
// These three cannot produce the pending-forever shape: the two browser ones
// share `BrowserPersistenceClass._isAvailable` (`:7378`), a synchronous
// `storage` read in a try/catch, and in-memory has no storage at all.
//
// ⚠️ WHAT THAT DOES NOT CLOSE. Auth's own persistence selection cannot hang on
// IndexedDB any more; auth REQUESTS still await two IndexedDB opens outside it —
// the heartbeat header and the App Check token (`_getAdditionalHeaders`). The
// heartbeat door is bounded at the open, by `installIndexedDbOpenBound()` above,
// and never on auth's own path. The App Check store is deliberately NOT bounded:
// its expiry reads as "no cached token" and can sign a user out. Both are in
// ./indexedDbOpenBound.ts (KB-3467).
//
// KB-3593: must run before `initializeAuth`, which may clear the stored user.
captureBootAuthSnapshot(firebaseConfig.apiKey);
export const auth = initializeAuth(app, {
  persistence: [
    browserLocalPersistence,
    browserSessionPersistence,
    inMemoryPersistence,
  ],
  // KB-1336 (marketing's src/lib/auth.ts:681) omits this to stop Firebase
  // eagerly loading the authDomain's /__/auth/iframe on passive page loads.
  //
  // ⛔ pro CANNOT take that omission, and an earlier revision of this file did,
  // carrying a comment that claimed `signInWithPopup` passes the resolver at
  // its call site. It does not. All three call sites — AuthContext.tsx:22,
  // LoginPage.tsx:131, CreateAccountPage.tsx:283 — call
  // `signInWithPopup(auth, provider)` with two arguments, and
  // `_withDefaultResolver` (`:9301`) then asserts on
  // `auth._popupRedirectResolver` and rejects `auth/argument-error`. Marketing's
  // omission is safe because marketing passes a resolver explicitly; pro's
  // would have broken Google sign-in on both doors, rendered as a "please try
  // again" toast for a permanently reproducible failure. Verified by grepping
  // pro's own call sites, not by trusting the prior art's comment.
  popupRedirectResolver: browserPopupRedirectResolver,
});

// KB-2606: routed through the KB-648/KB-469 transport-init helper (ported
// from marketing's src/lib/firebase-firestore.ts) so Safari/WebKit gets
// experimentalForceLongPolling instead of hitting the SDK's 15-20s
// auto-detect window — see src/lib/firestore-transport.ts.
export const db = initFirestoreWithTransport(app, { initializeFirestore, getFirestore });

// 🔥 Enhanced Firebase App Check initialization with proper error handling
const initializeFirebaseAppCheck = () => {
  if (!appCheckConfig.recaptchaSiteKey) {
    console.log('[AppCheck] No reCAPTCHA site key — skipping App Check (staging, unenforced)');
    return null;
  }

  try {
    console.log('[AppCheck] Initializing Firebase App Check...');
    console.log('[AppCheck] Development mode:', appCheckConfig.isDevelopment);

    // 🔥 PRODUCTION MODE ONLY - NO DEBUG TOKENS
    console.log('[AppCheck] Running in PRODUCTION mode - using real ReCaptcha verification');
    
    // Explicitly ensure no debug tokens are set
    delete (window as any).FIREBASE_APPCHECK_DEBUG_TOKEN;

    // Initialize App Check with ReCaptcha V3
    const appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(appCheckConfig.recaptchaSiteKey),
      isTokenAutoRefreshEnabled: true // 🔥 CRITICAL: Enable automatic token refresh
    });

    console.log('[AppCheck] Successfully initialized with auto-refresh enabled');
    console.log('[AppCheck] Using ReCaptcha site key:', appCheckConfig.recaptchaSiteKey.substring(0, 20) + '...');

    // KB-3352: expose the attestation outcome to the login seam, read-only.
    // BOTH callbacks, because a refusal reaches only the error one — the
    // bare-function listener below is never called on a 403. Registered before
    // the page-load mint so its outcome is known by the time anyone signs in.
    watchAppCheckOutcome((next, error) => onTokenChanged(appCheck, next, error));

    // 🔥 Enhanced token change listener with exponential backoff retry logic
    onTokenChanged(appCheck, (token) => {
      if (token) {
        console.log('[AppCheck] Token refreshed successfully');
        console.log('[AppCheck] Token expires at:', new Date(Date.now() + (60 * 60 * 1000))); // Estimate 1 hour
      } else {
        console.warn('[AppCheck] Token is null - implementing smart retry strategy');
        
        // Implement exponential backoff for token refresh attempts
        let retryDelay = 2000; // Start with 2 seconds
        const maxRetries = 5;
        const maxDelay = 60000; // Cap at 1 minute
        let retryCount = 0;
        
        const retryTokenRefresh = async () => {
          if (retryCount >= maxRetries) {
            console.error('[AppCheck] Max retries reached for token refresh - giving up');
            return;
          }
          
          try {
            console.log(`[AppCheck] Retry attempt ${retryCount + 1}/${maxRetries} after ${retryDelay}ms delay`);
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            
            const newToken = await getToken(appCheck, true);
            
            if (newToken) {
              console.log('[AppCheck] Token refresh successful after retry');
              return;
            } else {
              console.warn('[AppCheck] Token refresh returned null, will retry');
            }
          } catch (error: any) {
            console.error(`[AppCheck] Retry ${retryCount + 1} failed:`, error);
            
            // Handle specific error types with different strategies
            if (error.code === 'appCheck/throttled') {
              console.error('[AppCheck] Throttled - using longer delay for next retry');
              retryDelay = Math.min(retryDelay * 3, maxDelay); // Triple delay for throttling
            } else if (error.code === 'appCheck/fetch-status-error') {
              console.error('[AppCheck] ReCaptcha validation failed - using moderate delay');
              retryDelay = Math.min(retryDelay * 2, maxDelay); // Double delay for validation errors
            } else {
              console.error('[AppCheck] Unknown error - using standard exponential backoff');
              retryDelay = Math.min(retryDelay * 1.5, maxDelay); // Standard backoff
            }
          }
          
          retryCount++;
          
          // Continue retrying unless we hit max retries
          if (retryCount < maxRetries) {
            setTimeout(retryTokenRefresh, 1000); // Small delay before scheduling next retry
          }
        };
        
        // Start the retry process
        retryTokenRefresh();
      }
    });

    // 🔥 Enhanced initial token acquisition with specific 400 error handling
    const getInitialToken = async (retryCount = 0) => {
      try {
        const token = await getToken(appCheck, false);
        console.log('[AppCheck] Initial token obtained on page load');
        return token;
      } catch (error: any) {
        console.error('[AppCheck] Failed to get initial token:', error);
        
        if (error.code === 'appCheck/throttled') {
          console.error('[AppCheck] Requests are throttled. This usually happens after too many failed attempts.');
          throw new Error('App Check requests are throttled. Please wait and try again later.');
        } else if (error.code === 'appCheck/fetch-status-error') {
          console.error('[AppCheck] ReCaptcha validation failed (400 error). Possible causes:');
          console.error('[AppCheck] 1. ReCaptcha site key domain mismatch');
          console.error('[AppCheck] 2. ReCaptcha score too low');
          console.error('[AppCheck] 3. ReCaptcha token expired');
          console.error('[AppCheck] 4. Site key not configured for reCAPTCHA v3');
          throw new Error('ReCaptcha validation failed. Please check your configuration.');
        }
        
        if (retryCount < 2) {
          console.log('[AppCheck] Retrying token acquisition...');
          await new Promise(resolve => setTimeout(resolve, 2000 * (retryCount + 1))); // Longer delay for ReCaptcha
          return getInitialToken(retryCount + 1);
        }
        
        throw error;
      }
    };

    // Get initial token
    getInitialToken().catch((error) => {
      console.error('[AppCheck] Failed to initialize App Check token:', error);
    });

    // 🔥 Proactive keep-alive, resume/online refresh, and cross-tab coordination
    setupAppCheckKeepAlive(appCheck);

    return appCheck;
  } catch (error: any) {
    console.error('[AppCheck] Failed to initialize App Check:', error);
    
    // Provide helpful error messages
    if (error.code === 'appCheck/recaptcha-error') {
      console.error('[AppCheck] ReCaptcha error. Check your site key configuration.');
    } else if (error.code === 'appCheck/throttled') {
      console.error('[AppCheck] Too many requests. Please wait before trying again.');
    }
    
    // Don't throw - let the app continue without App Check if it fails
    // This prevents the app from breaking completely
    return null;
  }
};

// 🔥 Initialize App Check
export const appCheck = initializeFirebaseAppCheck();

// 🔥 Proactive keep-alive + resume/online refresh + cross-tab coordination
function setupAppCheckKeepAlive(instance: AppCheck) {
  const REFRESH_MS = 55 * 60 * 1000; // refresh slightly before 60m default TTL
  let timer: number | undefined;

  const refresh = async (force = false) => {
    try {
      await getToken(instance, force);
    } catch (e) {
      console.warn('[AppCheck] Background refresh failed', e);
    }
  };

  const start = () => {
    stop();
    // attempt a non-forced refresh on start/visibility
    void refresh(false);
    timer = window.setInterval(() => { void refresh(true); }, REFRESH_MS);
  };

  const stop = () => {
    if (timer) {
      clearInterval(timer);
      timer = undefined;
    }
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') start(); else stop();
  });
  window.addEventListener('focus', () => { void refresh(false); });
  window.addEventListener('online', () => { void refresh(true); });

  // Cross-tab broadcast so other tabs pick up fresh token promptly
  const hasBroadcast = typeof BroadcastChannel !== 'undefined';
  const bc = hasBroadcast ? new BroadcastChannel('appcheck') : null;
  if (bc) {
    onTokenChanged(instance, () => {
      try { bc!.postMessage({ type: 'token-refreshed', ts: Date.now() }); } catch {
        // intentionally no-op: cross-tab broadcast best-effort
        void 0;
      }
    });
    bc.onmessage = (e) => {
      if (e?.data?.type === 'token-refreshed') {
        void refresh(false);
      }
    };
  }

  if (document.visibilityState === 'visible') start();
}

// 🔥 Enhanced utility function to manually refresh App Check token
export const refreshAppCheckToken = async () => {
  if (!appCheck) {
    console.warn('[AppCheck] App Check not initialized - cannot refresh token');
    return false;
  }

  try {
    console.log('[AppCheck] Manually refreshing token...');
    const token = await getToken(appCheck, true); // Force refresh
    console.log('[AppCheck] Manual token refresh successful');
    return true;
  } catch (error: any) {
    console.error('[AppCheck] Manual token refresh failed:', error);
    
    if (error.code === 'appCheck/throttled') {
      console.error('[AppCheck] Requests are throttled. Please wait before trying again.');
    }
    
    return false;
  }
};

// 🔥 Ensure a fresh App Check token (attempt non-forced, then forced)
export const ensureFreshAppCheckToken = async (): Promise<void> => {
  if (!appCheck) return;
  try {
    await getToken(appCheck as AppCheck, false);
  } catch {
    await getToken(appCheck as AppCheck, true);
  }
};

// 🔥 Wrapper to retry once after forced refresh when App Check-related errors occur
export async function withAppCheckRetry<T>(op: () => Promise<T>): Promise<T> {
  try {
    return await op();
  } catch (err: any) {
    const code = err?.code;
    const msg = String(err?.message || '');
    const looksLikeAppCheck = code === 'permission-denied' || code === 'failed-precondition' || msg.includes('App Check');
    if (looksLikeAppCheck) {
      await refreshAppCheckToken();
      return await op();
    }
    throw err;
  }
}

// 🔥 NEW: Utility function to check App Check status
export const getAppCheckStatus = async () => {
  if (!appCheck) {
    return { initialized: false, hasToken: false, error: 'App Check not initialized' };
  }

  try {
    const token = await getToken(appCheck, false);
    return { 
      initialized: true, 
      hasToken: !!token, 
      tokenPreview: token ? token.token.substring(0, 20) + '...' : null 
    };
  } catch (error: any) {
    return { 
      initialized: true, 
      hasToken: false, 
      error: error.message || 'Failed to get token' 
    };
  }
};

// User roles types
export type UserRole = 'admin_user' | 'camp_user' | 'camp_manager' | 'camp_contributor';

export interface UserProfile {
  uid: string;
  email: string;
  role: UserRole;
  // When true, the Firestore user document had no 'role' field
  roleMissing?: boolean;
  tier?: string | null;
  firstName?: string;
  lastName?: string;
  organization?: DocumentReference;  // Changed from organizationId
  locationAccessGroupId?: string;
  lastLogin?: Date | Timestamp | null;
  // Derived/admin-only fields (not guaranteed to exist on the user doc)
  lastActiveAt?: Date | Timestamp | null;
  uiActionsCount?: number | null;
  daysActive?: number | null;
  createdAt?: Date | Timestamp | null;
  /** Set when anonymous data was migrated; used for users missing createdAt. */
  anonDataMigratedAt?: Date | Timestamp | null;
  /** Set when user was upgraded from invite (persistInviteToAccount). */
  upgradedAt?: Date | Timestamp | null;
  upgradedFromInviteToken?: string | null;
  onboarding?: Record<string, any> | null;
  /** Best-effort from Firestore (migration); Auth-backed value comes from admin callable. */
  authProvider?: string | null;
  /** Test / staff tagging on the user doc (e.g. internal tooling). */
  internal?: number | boolean | string | null;

  // Legacy fields - kept for backward compatibility
  organizationRole?: string;
  organizationId?: string;
}

// Re-export the pure status utilities so existing call sites can keep
// importing from `@/services/firebaseService`. The canonical home is
// `./account-request-status.ts` — kept separate so tests can import the
// pure utilities without the firebase mock in `tests/setup.ts` swallowing
// them.
export {
  ACCOUNT_REQUEST_STATUSES,
  normalizeAccountRequestStatus,
};
export type {
  AccountRequestStatus,
  AccountRequestStatusInput,
};

export type AccountRequestSource =
  | 'footer'
  | 'direct_route'
  | 'app_cta'
  | 'contact_redirect'
  | 'other';

export interface AccountRequestLocation {
  name?: string;
  address1?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  websiteUrl?: string;
  latLng?: { lat: number; lng: number };
}

export interface AccountRequestComment {
  at?: Date | Timestamp;
  from?: 'staff' | 'requester';
  fromName?: string;
  message?: string;
}

export interface AccountRequest {
  id?: string;
  // Legacy (always present)
  email: string;
  organizationName: string;
  organizationWebsite: string;
  status: AccountRequestStatus;
  createdAt: Date | Timestamp;
  notes?: string;
  // Marketing-side extension (KB-828 schema)
  requesterUid?: string;
  contactFirstName?: string;
  contactLastName?: string;
  contactRole?: 'director' | 'owner' | 'coordinator' | 'marketing' | 'other';
  contactPhone?: string;
  briefDescription?: string;
  yearFounded?: number;
  isNonProfit?: boolean;
  isFranchise?: boolean;
  locations?: AccountRequestLocation[];
  ageMin?: number;
  ageMax?: number;
  themes?: string[];
  seasonality?: 'year_round' | 'summer' | 'school_year' | 'other';
  typicalSessionSize?: number;
  whyJoinReason?: string;
  logoUrl?: string;
  heroPhotoUrl?: string;
  termsAcceptedAt?: Date | Timestamp;
  privacyAcceptedAt?: Date | Timestamp;
  marketingConsentAt?: Date | Timestamp | null;
  // Staff workflow
  assignedTo?: string;
  reviewerNotes?: string;
  publicComms?: AccountRequestComment[];
  linkedCampOrganizationId?: string;
  // Attribution
  source?: AccountRequestSource;
  refUrl?: string;
  utm?: { source?: string; medium?: string; campaign?: string };
  // Anti-spam
  honeypotFlagged?: boolean;
  // Timing
  submittedAt?: Date | Timestamp;
  updatedAt?: Date | Timestamp;
}

// Updated function to test custom claims setup using the callable function
export const testSetUserCustomClaims = async (
  uid: string, 
  role: UserRole, 
  organizationId?: string
) => {
  try {
    // Get the current auth user (must be admin user)
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("You must be logged in to perform this action");
    }
    
    // Initialize the Firebase Functions instance for us-east4 region
    const functions = getFunctions(app, "us-east4");
    
    // Create a callable function reference
    const setUserCustomClaims = httpsCallable(functions, 'setUserCustomClaims3');
    
    // Prepare request data
    const requestData: {
      uid: string;
      role: UserRole;
      organizationId?: string;
    } = { 
      uid,
      role
    };
    
    if (organizationId) {
      requestData.organizationId = organizationId;
    }
    
    // Call the Cloud Function
    const result = await setUserCustomClaims(requestData);
    console.log("Custom claims set result:", result.data);
    
    return { success: true, data: result.data };
  } catch (error) {
    console.error("Error in testSetUserCustomClaims:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error)
    };
  }
};

// Submit account request
export const submitAccountRequest = async (requestData: Omit<AccountRequest, 'createdAt' | 'status'>) => {
  console.log("Submitting account request:", requestData);
  try {
    // Validate required fields
    if (!requestData.email || !requestData.organizationName || !requestData.organizationWebsite) {
      throw new Error("Missing required fields in account request");
    }
    
    // Create reference to the accountRequests collection
    const accountRequestsRef = collection(db, "accountRequests");
    
    // Prepare the request data with server timestamp
    const newRequest = {
      ...requestData,
      status: 'pending',
      createdAt: serverTimestamp()
    };
    
    // Add the document to the collection
    const docRef = await addDoc(accountRequestsRef, newRequest);
    console.log("Account request submitted successfully with ID:", docRef.id);
    
    // Return the created request with its ID
    return { id: docRef.id, ...newRequest, createdAt: new Date() };
  } catch (error) {
    console.error("Error submitting account request:", error);
    throw error;
  }
};

// Get all account requests (for admins)
export const getAccountRequests = async () => {
  console.log("Fetching account requests...");
  try {
    const accountRequestsRef = collection(db, "accountRequests");
    const querySnapshot = await getDocs(accountRequestsRef);
    
    if (querySnapshot.empty) {
      console.log("No account requests found");
      return [];
    }
    
    // Map the documents to the AccountRequest interface. Normalize timestamps
    // for the legacy createdAt field and the new submittedAt/updatedAt fields,
    // and fold the legacy `'rejected'` status into `'declined'` so the UI
    // never has to special-case it.
    const requests = querySnapshot.docs.map(doc => {
      const data = doc.data() as Record<string, unknown>;
      const createdAt = data.createdAt instanceof Timestamp
        ? data.createdAt.toDate()
        : (data.createdAt as Date | undefined) || new Date();
      return {
        ...data,
        id: doc.id,
        createdAt,
        status: normalizeAccountRequestStatus(data.status as string | undefined),
      } as AccountRequest;
    });
    
    console.log(`Retrieved ${requests.length} account requests`);
    return requests;
  } catch (error) {
    console.error("Error getting account requests:", error);
    throw error;
  }
};

// Update account request status. Accepts the legacy 'rejected' value at the
// boundary and normalizes to 'declined' for the write — that way callers
// (legacy ActionDialog wired to approve/reject) keep working while new code
// can pass any of the 7 canonical statuses.
export const updateAccountRequestStatus = async (
  id: string,
  status: AccountRequestStatusInput,
  notes?: string,
) => {
  const writeStatus = status === 'rejected' ? 'declined' : status;
  console.log(`Updating account request ${id} to status: ${writeStatus}`);
  try {
    const requestRef = doc(db, "accountRequests", id);
    const requestDoc = await getDoc(requestRef);

    if (!requestDoc.exists()) {
      throw new Error(`Account request with ID ${id} not found`);
    }

    const updateData: {
      status: AccountRequestStatus;
      notes?: string;
      updatedAt: ReturnType<typeof serverTimestamp>;
    } = {
      status: writeStatus,
      updatedAt: serverTimestamp(),
    };
    if (notes) {
      updateData.notes = notes;
    }

    await setDoc(requestRef, updateData, { merge: true });
    console.log(`Account request ${id} updated successfully`);
    return true;
  } catch (error) {
    console.error("Error updating account request:", error);
    throw error;
  }
};

export const updateAccountRequestAssignee = async (
  id: string,
  assignedTo: string | null,
) => {
  try {
    const requestRef = doc(db, "accountRequests", id);
    await setDoc(
      requestRef,
      { assignedTo: assignedTo ?? null, updatedAt: serverTimestamp() },
      { merge: true },
    );
    return true;
  } catch (error) {
    console.error("Error updating account request assignee:", error);
    throw error;
  }
};

export const updateAccountRequestReviewerNotes = async (
  id: string,
  reviewerNotes: string,
) => {
  try {
    const requestRef = doc(db, "accountRequests", id);
    await setDoc(
      requestRef,
      { reviewerNotes, updatedAt: serverTimestamp() },
      { merge: true },
    );
    return true;
  } catch (error) {
    console.error("Error updating account request reviewer notes:", error);
    throw error;
  }
};

/** Lightweight shape for the assignee picker — uid + display + email only. */
export interface AdminUserOption {
  uid: string;
  displayName: string;
  email?: string;
}

/**
 * Pulls users whose Firestore profile carries `role === 'admin_user'`. The
 * pro AuthContext also recognizes hard-coded admin UIDs and token claims for
 * legacy reasons, but for the assignee picker we want the manageable list
 * (anyone the Firestore role assigns). Callers can supplement this with
 * static known-admin UIDs if needed.
 *
 * ADMIN-ONLY — do not call this from a camp_manager / camp_contributor
 * surface. KB-2306: the narrowed `users/{userId}` read rule (KB-2291) admits a
 * LIST only when the query carries `where("organizationId","==",<caller's
 * org>)`; this one filters on `role` instead, so any non-admin caller is
 * denied. The rule cannot be satisfied by adding an org filter either — an
 * admin_user has no `organizationId`, so "admins of my org" is not a
 * expressible set. If a non-admin surface ever needs this list, move it to a
 * callable on the Admin SDK rather than widening the rule.
 */
export const listAdminUsers = async (): Promise<AdminUserOption[]> => {
  try {
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("role", "==", "admin_user"));
    const snap = await getDocs(q);
    return snap.docs
      .map((d) => {
        const data = d.data() as Record<string, unknown>;
        const first = (data.firstName as string) || '';
        const last = (data.lastName as string) || '';
        const composed = `${first} ${last}`.trim();
        return {
          uid: d.id,
          displayName: composed || (data.email as string) || d.id,
          email: data.email as string | undefined,
        };
      })
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  } catch (error) {
    console.error("Error listing admin users:", error);
    return [];
  }
};

// Check if there is an approved account request for a given email
export const hasApprovedAccountRequest = async (email: string): Promise<boolean> => {
  try {
    const accountRequestsRef = collection(db, "accountRequests");
    const q = query(accountRequestsRef, where("email", "==", email), where("status", "==", "approved"));
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    console.error("Error checking approved account request:", error);
    return false;
  }
};

// Get user profile by UID with PII data
export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  try {
    const userRef = doc(db, "users", uid);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      
      // Fetch PII data from subcollection
      const piiRef = doc(db, "users", uid, "private", "pii");
      const piiDoc = await getDoc(piiRef);
      const piiData = piiDoc.exists() ? piiDoc.data() : {};
      
      return { 
        uid, 
        ...userData,
        firstName: piiData.firstName || '',
        lastName: piiData.lastName || ''
      } as UserProfile;
    }
    return null;
  } catch (error) {
    console.error("Error getting user profile:", error);
    return null;
  }
};

/**
 * Owner-scoped profile read for the sign-in allowlist gate. Returns `null` only
 * when the caller genuinely has no profile doc; any Firestore failure
 * (permission-denied, offline, quota) THROWS.
 *
 * KB-2306: the login gate previously called a `getUserProfileByEmail` helper
 * that ran `query(users, where("email","==",email))` and swallowed errors to
 * `null`. Under the narrowed `users/{userId}` read rule (KB-2291) that
 * unfiltered LIST is denied to every non-admin, and because it ran before any
 * role was known no role-based carve-out could ever satisfy it. The swallow
 * turned the denial into "no profile", which the caller reads as "not on the
 * allowlist" — signing every camp_manager / camp_contributor / camp_user out
 * with "Your account is not authorized."
 *
 * The email lookup was never needed: `users` is uid-keyed everywhere (no
 * `addDoc` on the collection in this app, and every server write is
 * `.doc(uid)`), so the doc the email query found is the same doc `uid` names.
 * Reading it by uid satisfies the rule's owner clause, needs no server round
 * trip and no index, and keeps working if the account's email later changes.
 */
export const getOwnUserProfile = async (uid: string): Promise<UserProfile | null> => {
  const userDoc = await getDoc(doc(db, "users", uid));
  if (!userDoc.exists()) return null;

  const piiDoc = await getDoc(doc(db, "users", uid, "private", "pii"));
  const piiData = piiDoc.exists() ? piiDoc.data() : {};

  return {
    uid,
    ...userDoc.data(),
    firstName: piiData.firstName || '',
    lastName: piiData.lastName || ''
  } as UserProfile;
};

// Create or update user profile with PII data
export const setUserProfile = async (profile: UserProfile) => {
  try {
    const batch = writeBatch(db);
    
    // Separate PII data from other profile data
    const { firstName, lastName, ...otherProfileData } = profile;
    
    // Update main user document
    const userRef = doc(db, "users", profile.uid);
    batch.set(userRef, otherProfileData, { merge: true });
    
    // Update PII data in subcollection
    if (firstName !== undefined || lastName !== undefined) {
      const piiRef = doc(db, "users", profile.uid, "private", "pii");
      const piiData: any = {};
      
      if (firstName !== undefined) {
        piiData.firstName = firstName || null;
      }
      if (lastName !== undefined) {
        piiData.lastName = lastName || null;
      }
      
      batch.set(piiRef, piiData, { merge: true });
    }
    
    await batch.commit();
    return true;
  } catch (error) {
    console.error("Error setting user profile:", error);
    return false;
  }
};
